import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldX, Loader2, ArrowLeft } from "lucide-react";
import api from "../panel/api";
import { EASE } from "@/components/Motion";
import { Logo } from "@/components/Logo";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function VerifyStatementPage() {
  const { statementId } = useParams();
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/statements/verify/${statementId}`)
      .then(({ data }) => setResult(data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [statementId]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-4 py-16">
      <div className="pointer-events-none absolute -top-32 left-1/3 h-[480px] w-[480px] rounded-full bg-gold/[0.07] blur-[140px]" />
      <div className="noise-overlay pointer-events-none fixed inset-0 opacity-[0.05]" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative w-full max-w-lg"
        data-testid="verify-statement-card"
      >
        <Link to="/" data-testid="verify-back-link"
          className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-neutral-500 transition-colors hover:text-goldlight">
          <ArrowLeft size={14} /> BACK TO SITE
        </Link>

        <div className="rounded-3xl border border-gold/15 bg-panel/80 p-8 backdrop-blur-xl sm:p-10">
          <div className="flex items-center gap-3">
            <Logo className="h-11 w-11 rounded-xl object-cover ring-1 ring-gold/40" />
            <div>
              <div className="text-sm font-bold tracking-[0.2em] text-neutral-100">
                BEATKING<span className="text-gold">SMM</span>
              </div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-gold/70">STATEMENT VERIFICATION</div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 size={28} className="animate-spin text-gold" data-testid="verify-loading" />
            </div>
          ) : failed || !result?.valid ? (
            <div className="mt-8 text-center" data-testid="verify-invalid">
              <ShieldX size={44} className="mx-auto text-rose-400" />
              <h1 className="mt-5 font-serif text-3xl italic text-neutral-50">Not verified.</h1>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                Statement <span className="font-mono text-goldlight">{statementId}</span> was not found in the
                BEATKINGSMM system. It may have been altered or was never issued by us.
              </p>
            </div>
          ) : (
            <div className="mt-8" data-testid="verify-valid">
              <div className="text-center">
                <ShieldCheck size={44} className="mx-auto text-emerald-400" />
                <h1 className="mt-5 font-serif text-3xl italic text-neutral-50">Authentic statement.</h1>
                <p className="mt-2 text-sm text-neutral-400">
                  Verified against the BEATKINGSMM system at generation time.
                </p>
              </div>

              <div className="mt-8 space-y-2.5 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                {[
                  ["Statement ID", result.statement_id],
                  ["Generated", result.generated_at],
                  ["Account holder", result.account_holder],
                  ["Account email", result.account_email],
                  ["Orders", result.orders_count],
                  ["Transactions", result.transactions_count],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-4 text-sm">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{k}</span>
                    <span className="text-neutral-200" data-testid={`verify-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</span>
                  </div>
                ))}
                <div className="my-3 border-t border-white/5" />
                {[
                  ["Total credits", inr(result.total_credits)],
                  ["Total debits", inr(result.total_debits)],
                  ["Refunds credited", inr(result.total_refunds)],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-4 text-sm">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{k}</span>
                    <span className="text-neutral-200">{v}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">Closing balance</span>
                  <span className="text-base font-bold text-goldlight" data-testid="verify-closing-balance">{inr(result.closing_balance)}</span>
                </div>
                <div className="pt-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Content fingerprint (SHA-256)</span>
                  <p className="mt-1 break-all font-mono text-[10px] leading-relaxed text-neutral-600" data-testid="verify-hash">{result.content_hash}</p>
                </div>
              </div>

              <p className="mt-6 text-center text-[11px] leading-relaxed text-neutral-600">
                This is a computer-generated statement issued by BEATKINGSMM. It reflects transactions recorded in the
                BEATKINGSMM system and is not a bank-issued statement or bank document.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
